<template>
    <info-widget :collapsible="true" :is-expanded="expanded" @toggle_expand="$emit('toggle_expand')">
        <template v-slot:title>
            E-Verify
        </template>

        <template v-slot:title-detail>
            <span v-if="$permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.EVERIFY_ROLES)">
                <h4 class="text-dark mb-0">
                    <i>{{ latestEVerifyCaseStatusDisplay }}</i>
                </h4>
            </span>
        </template>

        <slot v-if="!$permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.EVERIFY_ROLES)">
            <div class="info-widget-empty p-2">
                This information is not available to your security role.
            </div>
        </slot>

        <slot v-if="$permissions.hasRolesInCompany(client.id, employee.company_id, $store.state.security_roles.EVERIFY_ROLES)">
            <div v-if="latestEVerifyCase">
                <div class="info-widget p-2" v-if="latestEVerifyCase.case_creation_errors.errors">
                    <div class="item-details-banner--danger p-2">
                        <span class="text-danger-dark text-bold">Errors creating case:</span>

                        <div v-for="err in latestEVerifyCase.case_creation_errors.errors" class="my-2">
                            <span class="text-danger-dark text-bold">
                                <span v-if="err.attribute_name">{{ err.attribute_name }}: </span>
                                {{ err.message }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'PRE_DRAFT'">
                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal" v-if="otherEVerifyCases.length == 0">
                        <i class="far fa-fw fa-pencil mr-1"></i> Create E-Verify Case
                    </a>

                    <a href class="btn btn-outline-primary mx-2" @click.prevent="openEVerifyCaseModal('OPEN_NEW_CASE')" v-if="otherEVerifyCases.length > 0">
                        <i class="far fa-fw fa-pencil mr-1"></i> Open New Case
                    </a>

                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'DRAFT'">
                    <div class="info-widget-empty pb-2 text-left">
                        This case is in DRAFT status and has not been submitted. Continue below to submit the case.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> Submit E-Verify Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'UNCONFIRMED_DATA'">
                    <div class="info-widget-empty pb-2 text-left">
                        This case has unconfirmed data that requires review before submission.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> Review Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'MANUAL_REVIEW'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        E-Verify needs more time to confirm that this employee is authorized to work in the United States. You should not take any action against the employee while E-Verify is reviewing this case.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> View Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'PHOTO_MATCH'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        <b>Action required:</b> Match photo against employee's photo document
                    </div>

                    <a href class="btn btn-primary" @click.prevent="openEVerifyCaseModal('PHOTO_MATCH')">
                        <i class="far fa-fw fa-portrait mr-1"></i> Match Photo
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'SCAN_AND_UPLOAD'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        <b>Action required:</b> Scan and upload employee's photo document
                    </div>

                    <a href class="btn btn-primary" @click.prevent="openEVerifyCaseModal('SCAN_AND_UPLOAD')">
                        <i class="far fa-fw fa-portrait mr-1"></i> Scan and Upload
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'PENDING_REFERRAL'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        It's okay! E-Verify just needs some more information from you and the employee before confirming employment authorization. Here are the next steps you both will need to take.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal('TNC')">
                        <i class="far fa-fw fa-pencil mr-1"></i> View Next Steps
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'REFERRED'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        This employee has been referred to DHS/SSA to resolve a mismatch.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal('REFERRED')">
                        <i class="far fa-fw fa-pencil mr-1"></i> View Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'FINAL_NONCONFIRMATION'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}</b>
                        <br><br>

                        E-Verify was not able to verify employment eligibility.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal('FNC')">
                        <i class="far fa-fw fa-pencil mr-1"></i> Close Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'QUEUED'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}: {{ latestEVerifyCase.get_case_eligibility_statement_display }}</b>
                        <br><br>

                        E-Verify needs more time to confirm that this employee is authorized to work in the United States. You should not take any action against the employee while E-Verify is reviewing this case.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> View Case
                    </a>
                </div>

                <div class="p-2 text-center" v-if="latestEVerifyCase.case_status == 'CLOSE_CASE_AND_RESUBMIT'">
                    <div class="info-widget-empty pb-2 text-left">
                        <b>{{ latestEVerifyCase.everify_case_status_display }}: {{ latestEVerifyCase.get_case_eligibility_statement_display }}</b>
                        <br><br>

                        Something is incorrect in the data submitted. E-Verify needs you to close this case and re-enter the employee's information in a new case.
                    </div>

                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal('CLOSE_CASE')">
                        <i class="far fa-fw fa-pencil mr-1"></i> Close Case
                    </a>
                </div>

                <div class="p-2 pb-3 text-center" v-if="latestEVerifyCase.case_status == 'CLOSED'">
                    <div class="info-widget-empty pb-2 text-left" v-if="latestEVerifyCase.onboarding_canceled_on">
                        Onboarding canceled, no E-Verify case created
                    </div>
                    <div class="info-widget-empty pb-2 text-left" v-else>
                        <div v-if="latestEVerifyCase.get_case_eligibility_statement_display">
                            <b>{{ latestEVerifyCase.everify_case_status_display }}: {{ latestEVerifyCase.get_case_eligibility_statement_display }}</b>
                            <br><br>
                        </div>

                        This case has been closed.
                    </div>

                    <a href class="btn btn-outline-primary mx-2" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> View Case
                    </a>

                    <a href class="btn btn-outline-primary mx-2" @click.prevent="openEVerifyCaseModal('OPEN_NEW_CASE')">
                        <i class="far fa-fw fa-pencil mr-1"></i> Open New Case
                    </a>
                </div>

                <div class="px-2 pb-2" v-if="otherEVerifyCases.length > 0">
                    <hr class="mt-0 mb-3">
                    <h4 class="theading px-2 mb-2"><b>PREVIOUS CASES</b></h4>

                    <table class="table css-table widget-table table-fixed" v-if="otherEVerifyCases.length">
                        <thead>
                            <tr>
                                <th class="text-start">Case #</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody v-for="other_case in otherEVerifyCases">
                            <tr>
                                <td class="text-start text-nowrap">
                                    <a href class="" role="button" @click.prevent="openOtherEVerifyCaseModal(other_case)">
                                        {{ other_case.case_number }}
                                    </a>
                                </td>
                                <td>
                                    <span class="truncate-wrapper">
                                        {{ other_case.case_status_pretty }}
                                    </span>
                                </td>

                            </tr>
                        </tbody>
                    </table>

                </div>
            </div>

            <div v-if="!latestEVerifyCase">
                <div class="info-widget-empty p-2">
                    No case created yet
                </div>

                <div class="p-2 text-center">
                    <a href class="btn btn-outline-primary" @click.prevent="openEVerifyCaseModal">
                        <i class="far fa-fw fa-pencil mr-1"></i> Create E-Verify Case
                    </a>
                </div>

            </div>
        </slot>
    </info-widget>

    <e-verify-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyModalOpen" @close="EVerifyModalOpen = false" />

    <e-verify-modal :client="client" :company="employee.company" :employee="employee" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyNewCaseModalOpen" @close="EVerifyNewCaseModalOpen = false" :show-new-case-warning="true" />

    <e-verify-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="currentEVerifyCase" @updated="onEVerifyCaseUpdate($event)" v-if="currentEVerifyCase && EVerifyCurrentCaseModalOpen" @close="EVerifyCurrentCaseModalOpen = false; currentEVerifyCase = null;" />

    <e-verify-tnc-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyTNCModalOpen" @close="EVerifyTNCModalOpen = false" />

    <e-verify-fnc-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyFNCModalOpen" @close="EVerifyFNCModalOpen = false" />

    <e-verify-photo-match-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" :latest-i9-form="latestI9Form" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyPhotoMatchModalOpen" @close="EVerifyPhotoMatchModalOpen = false" />

    <e-verify-scan-and-upload-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" :latest-i9-form="latestI9Form" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyScanAndUploadModalOpen" @close="EVerifyScanAndUploadModalOpen = false" />

    <e-verify-close-case-modal :client="client" :company="employee.company" :employee="employee" :e-verify-case="latestEVerifyCase" @updated="onEVerifyCaseUpdate($event)" v-if="EVerifyCloseCaseModalOpen" @close="EVerifyCloseCaseModalOpen = false" />
</template>

<script>
import ButtonRow from '@/components/ButtonRow'
import InfoWidget from '@/components/InfoWidget'

import EVerifyModal from './EVerifyModal'
import EVerifyTNCModal from './EVerifyTNCModal'
import EVerifyFNCModal from './EVerifyFNCModal'
import EVerifyPhotoMatchModal from './EVerifyPhotoMatchModal'
import EVerifyScanAndUploadModal from './EVerifyScanAndUploadModal'
import EVerifyCloseCaseModal from './EVerifyCloseCaseModal'

export default {
    components: {
        ButtonRow, InfoWidget,
        EVerifyModal, EVerifyTncModal: EVerifyTNCModal, EVerifyFncModal: EVerifyFNCModal, EVerifyPhotoMatchModal, EVerifyScanAndUploadModal, EVerifyCloseCaseModal,
    },
    props: ['employee', 'client', 'eVerifyCases', 'onboardingApplications', 'expanded'],
    emits: ['updated', 'toggle_expand'],
    computed: {
        latestEVerifyCase() {
            const cases = this.eVerifyCases.slice()
            if (! cases) {
                return {}
            }

            return cases.toSorted((a, b) => {
                return (a.created_on > b.created_on) ? 1 : -1
            })[cases.length - 1]
        },
        otherEVerifyCases() {
            const cases = this.eVerifyCases.slice()
            if (! cases) {
                return []
            }

            // array without the latest (don't use pop bc it mutates); note order of sort
            return cases.toSorted((a, b) => {
                return (a.created_on < b.created_on) ? 1 : -1
            }).slice(1)
        },
        latestEVerifyCaseStatusDisplay() {
            // not *quite* the latest case, since the latest case may be in draft
            // but we have a previously Closed case, so don't want to show "No Case" in that case
            const cases = this.eVerifyCases.slice()

            if (cases.length == 0) {
                return "No Case"
            }

            cases.sort((a, b) => {
                return (a.created_on < b.created_on) ? 1 : -1
            })

            if (cases.length == 1) {
                if (cases[0].case_status == 'PRE_DRAFT') {
                    return "No Case"
                } else {
                    return cases[0].case_status_pretty
                }
            }

            return cases[1].case_status_pretty
        },
        latestI9Form() {
            if (!this.employee) {
                return null
            }

            // OA
            if (this.employee.is_onboarding_application) {
                return this.employee.forms.find(f => f.form_designation == 'i9')
            }

            // EE - find OA (if there is one)
            if (this.onboardingApplications.length > 0) {
                return this.onboardingApplications[0].forms.find(f => f.form_designation == 'i9')
            }

            return null;
        },
    },
    data() {
        return {
            currentEVerifyCase: null,
            EVerifyModalOpen: false,
            EVerifyNewCaseModalOpen: false,
            EVerifyCurrentCaseModalOpen: false,
            EVerifyTNCModalOpen: false,
            EVerifyFNCModalOpen: false,
            EVerifyPhotoMatchModalOpen: false,
            EVerifyScanAndUploadModalOpen: false,
            EVerifyCloseCaseModalOpen: false,
        }
    },
    methods: {
        openEVerifyCaseModal(action) {
            switch(action) {
                case 'TNC':
                    this.EVerifyTNCModalOpen = true
                    break;
                case 'FNC':
                    this.EVerifyFNCModalOpen = true
                    break;
                case 'REFERRED':
                    // TNC modal
                    this.EVerifyTNCModalOpen = true
                    break;
                case 'PHOTO_MATCH':
                    this.EVerifyPhotoMatchModalOpen = true
                    break;
                case 'SCAN_AND_UPLOAD':
                    this.EVerifyScanAndUploadModalOpen = true
                    break;
                case 'CLOSE_CASE':
                    this.EVerifyCloseCaseModalOpen = true
                    break;
                // explicitly open a new case
                case 'OPEN_NEW_CASE':
                    this.EVerifyNewCaseModalOpen = true
                    break;
                default:
                    this.EVerifyModalOpen = true;
            }
        },
        openOtherEVerifyCaseModal(other_case) {
            this.currentEVerifyCase = other_case
            this.EVerifyCurrentCaseModalOpen = true
        },
        onEVerifyCaseUpdate(evt) {
            this.$emit('updated', evt)
        },
    },
}
</script>
